import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, X, ChevronDown, Check } from 'lucide-react';
import { AthleteAvatar } from './AthleteAvatar';

/**
 * Searchable athlete selector that replaces plain dropdowns.
 * Supports single-select and multi-select modes.
 */
const SearchableAthleteSelect = ({
  athletes,
  value,            // string (single) or string[] (multi)
  onChange,          // (value) => void
  multi = false,
  placeholder = 'Select athlete...',
  label,
  disabled = false,
  className = '',
  'data-testid': testId = 'athlete-select',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [squadFilter, setSquadFilter] = useState('');
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  // Anchor coordinates for the portalled dropdown. Recomputed when the
  // dropdown opens AND on scroll/resize while it's open so it stays glued
  // to the trigger even inside modal flows.
  const [anchor, setAnchor] = useState(null);

  const recomputeAnchor = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    // Decide whether to flip above if there isn't enough room below.
    const dropdownH = 360; // approx max height of dropdown
    const spaceBelow = window.innerHeight - r.bottom;
    const flipUp = spaceBelow < dropdownH && r.top > dropdownH;
    setAnchor({
      left: r.left,
      width: r.width,
      top: flipUp ? Math.max(8, r.top - dropdownH - 4) : r.bottom + 4,
      placement: flipUp ? 'top' : 'bottom',
    });
  };

  // Open/close lifecycle: recompute anchor and listen for scroll/resize.
  useEffect(() => {
    if (!open) { setAnchor(null); return; }
    recomputeAnchor();
    const handler = () => recomputeAnchor();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open]);

  // Close on outside click — needs to also accept clicks on the portalled dropdown.
  // We rely on a DOM attribute walk (`[data-allow-focus-outside="true"]`) instead
  // of dropdownRef.contains() so the check is independent of when React commits
  // refs through the FocusScope wrapper.
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current) return;
      const insideTrigger = ref.current.contains(e.target);
      const insideAllowed = e.target?.closest && e.target.closest('[data-allow-focus-outside="true"]');
      if (!insideTrigger && !insideAllowed) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const squads = useMemo(() => {
    const s = new Set();
    athletes.forEach(a => { if (a.squad) s.add(a.squad); });
    return [...s].sort();
  }, [athletes]);

  const filtered = useMemo(() => {
    let result = athletes;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a => a.full_name?.toLowerCase().includes(q) || a.position?.toLowerCase().includes(q));
    }
    if (squadFilter) {
      result = result.filter(a => a.squad === squadFilter);
    }
    return result;
  }, [athletes, search, squadFilter]);

  const selectedValues = multi ? (value || []) : (value ? [value] : []);
  const selectedAthletes = athletes.filter(a => selectedValues.includes(a.id));

  const handleSelect = (id) => {
    if (multi) {
      const current = value || [];
      onChange(current.includes(id) ? current.filter(v => v !== id) : [...current, id]);
    } else {
      onChange(id);
      setOpen(false);
      setSearch('');
    }
  };

  const displayText = selectedAthletes.length === 0
    ? placeholder
    : multi
      ? `${selectedAthletes.length} athlete${selectedAthletes.length > 1 ? 's' : ''} selected`
      : selectedAthletes[0]?.full_name;

  return (
    <div ref={ref} className={`relative ${className}`} data-testid={testId}>
      {label && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>}
      <button
        type="button"
        ref={triggerRef}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`w-full flex items-center justify-between h-9 px-3 text-sm border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 transition-colors ${
          open ? 'ring-2 ring-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid={`${testId}-trigger`}
      >
        <span className={`truncate ${selectedAthletes.length === 0 ? 'text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
          {displayText}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Multi-select badges */}
      {multi && selectedAthletes.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {selectedAthletes.map(a => (
            <Badge key={a.id} variant="outline" className="text-[10px] px-1.5 py-0 gap-1 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
              {a.full_name}
              <X className="w-2.5 h-2.5 cursor-pointer hover:text-red-500" onClick={(e) => { e.stopPropagation(); handleSelect(a.id); }} />
            </Badge>
          ))}
        </div>
      )}

      {/* Dropdown — portalled to <body> with fixed positioning so it escapes
          modal overflow:hidden / clipping and renders above any DialogContent.
          Phase 73 fix: Radix FocusScope (trapped+loop) pauses the parent Dialog's
          FocusScope via the shared focusScopesStack while this dropdown is open,
          so typing focus stays inside the search input. data-allow-focus-outside
          opts the subtree out of Radix Dialog's DismissableLayer (prevents
          auto-close) and out of our own outside-click closer. */}
      {open && anchor && typeof document !== 'undefined' && createPortal(
        <FocusScope
          trapped
          loop
          onMountAutoFocus={(e) => {
            e.preventDefault();
            requestAnimationFrame(() => {
              searchInputRef.current?.focus();
            });
          }}
          onUnmountAutoFocus={(e) => {
            // Don't auto-return focus to the trigger — that would feed back
            // into the parent Dialog's FocusScope and steal focus from
            // whatever the user clicked next.
            e.preventDefault();
          }}
          ref={dropdownRef}
          data-allow-focus-outside="true"
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
          style={{
            position: 'fixed',
            left: anchor.left,
            top: anchor.top,
            width: anchor.width,
            zIndex: 9999,
            // Radix Dialog (modal) sets body { pointer-events: none } via its
            // DismissableLayer so only DialogContent is interactive. Re-enable
            // pointer events on our portalled dropdown so the search input
            // and option buttons remain clickable.
            pointerEvents: 'auto',
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testId}-dropdown`}
        >
          <div className="p-2 space-y-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                ref={searchInputRef}
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-xs"
                data-testid={`${testId}-search`}
              />
            </div>
            {squads.length > 1 && (
              <div className="flex gap-1 flex-wrap">
                <button
                  onClick={() => setSquadFilter('')}
                  className={`px-2 py-0.5 text-[10px] rounded-full transition-colors ${!squadFilter ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200'}`}
                >All</button>
                {squads.map(s => (
                  <button
                    key={s}
                    onClick={() => setSquadFilter(squadFilter === s ? '' : s)}
                    className={`px-2 py-0.5 text-[10px] rounded-full transition-colors ${squadFilter === s ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200'}`}
                    data-testid={`${testId}-squad-${s}`}
                  >{s}</button>
                ))}
              </div>
            )}
          </div>
          <div className="max-h-[240px] overflow-y-auto py-1">
            {(() => {
              const hasActiveSearch = search.trim() || squadFilter;
              const MAX_INITIAL = 10;
              const displayList = hasActiveSearch ? filtered : filtered.slice(0, MAX_INITIAL);
              const hiddenCount = !hasActiveSearch && filtered.length > MAX_INITIAL ? filtered.length - MAX_INITIAL : 0;
              
              if (filtered.length === 0) {
                return <p className="text-xs text-gray-400 text-center py-4">No athletes found</p>;
              }
              return (
                <>
                  {displayList.map(a => {
                    const isSelected = selectedValues.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        onClick={() => handleSelect(a.id)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                          isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        data-testid={`${testId}-option-${a.id}`}
                      >
                        {multi ? (
                          <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                            {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                        ) : null}
                        <AthleteAvatar
                          photoUrl={a.profile_photo}
                          thumbUrl={a.profile_photo_thumbnail}
                          name={a.full_name}
                          size="xs"
                          className="flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-800 dark:text-gray-200 truncate block">{a.full_name}</span>
                        </div>
                        {a.squad && (
                          <span className="text-[10px] text-gray-400 flex-shrink-0">{a.squad}</span>
                        )}
                        {a.position && (
                          <span className="text-[10px] text-gray-400 flex-shrink-0">{a.position}</span>
                        )}
                      </button>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center py-2 border-t border-gray-50 dark:border-gray-700">
                      Type to search {hiddenCount} more athletes...
                    </p>
                  )}
                </>
              );
            })()}
          </div>
          <div className="px-3 py-1.5 border-t border-gray-100 dark:border-gray-700 text-[10px] text-gray-400">
            {filtered.length} of {athletes.length} athletes
          </div>
        </FocusScope>,
        document.body,
      )}
    </div>
  );
};

export default SearchableAthleteSelect;
